import React, { useRef } from "react";
import { useFormContext } from "react-hook-form";
import { Col, Collapse, Form, Row } from "react-bootstrap";
import TextField from "../../reusable-form-fields/text-field";
import FileInput from "../../reusable-form-fields/input-file";
import SelectField from "../../reusable-form-fields/select-field";
import { CustomCard, FieldSet, Legend } from "../../common-styling";
import EditorModalController from "../../funnel-form/editor-modal-controller";
import { checkPhoneNumFieldFormatOnKeyDown } from "../../../services/common-service";
import ColorPickerWithTextField from "../../reusable-form-fields/color-picker-with-text-field";
import { useTranslation } from "react-i18next";
import { useTabRoute } from "../../../hooks/useTabRoute";
import {
  findLegalPageWithoutContent,
  legalPageUrls
} from "../../../services/website-legal-pages";

// Publish error of a legal text that is shown as a page but has no content;
// hidden as soon as the text is added or the switch is turned off
const LegalPageContentError = ({ error, isMissing }) => {
  const { t } = useTranslation();
  if (!error?.message || !isMissing) return null;
  return (
    <Form.Text className="text-danger d-block">{t(error.message)}</Form.Text>
  );
};

const FooterSection = ({
  logoTypes,
  parseResourceSrc,
  parsePreviewResourceSrc,
  sections,
  customerData,
  isFormChanged
}) => {
  const { t } = useTranslation();
  const {
    formState: { errors },
    control,
    watch,
    register,
    getValues
  } = useFormContext();

  const FooterBGColorDisplay = watch("footer.bgColorDisplay");

  const ContactCardBtnBGColorDisplay = watch(
    "footer.contact_card.btn.bgColorDisplay"
  );
  const ContactCardOptionalBtnBGColorDisplay = watch(
    "footer.contact_card.optionalBtn.bgColorDisplay"
  );
  const footerLogoType = watch("footer.logo.type");

  const footerLogoImage = watch("footer.logo.image");
  const footerContactCardImage = watch("footer.contact_card.image");

  const agbLinkEnabled = watch("footer.footer_links.pp_links.agbLinkEnabled");
  const showLegalPages = watch("footer.footer_links.pp_links.showAsPages");
  const websiteDomain = watch("domain");
  const { tabRoute } = useTabRoute();
  const legalLinks = watch("footer.footer_links.pp_links.links");
  const legalPpLinks = {
    showAsPages: showLegalPages,
    agbLinkEnabled,
    links: legalLinks
  };
  const legalPageWithoutContent = findLegalPageWithoutContent(legalPpLinks);
  const legalPageUrlList = legalPageUrls({
    domain: websiteDomain?.trim() || t("customerWebsite.legalPagesYourDomain"),
    tabRoute,
    ppLinks: legalPpLinks
  });

  const footerBgColorPickerRef = useRef();
  const footerLogoColorPickerRef = useRef();
  const footerTaglineColorPickerRef = useRef();
  const facebookColorPickerRef = useRef();
  const instaColorPickerRef = useRef();
  const youtubeColorPickerRef = useRef();
  const x_twitterColorPickerRef = useRef();
  const pinterestColorPickerRef = useRef();
  const tiktokColorPickerRef = useRef();
  const linkedinColorPickerRef = useRef();
  const footerQuickLinksColorPickerRef = useRef();
  const footerPolicyLinksColorPickerRef = useRef();
  const contactCardNameColorPickerRef = useRef();
  const contactCardTitleColorPickerRef = useRef();
  const contactCardCTAButtonColorPickerRef = useRef();
  const contactCardCTAOptionalButtonColorPickerRef = useRef();
  const contactCardCTAButtonBgColorPickerRef = useRef();
  const contactCardCTAOptionalButtonBgColorPickerRef = useRef();
  const contactCardPhoneColorPickerRef = useRef();
  const localSeoLinksColorPickerRef = useRef();

  const maxContentLength = 75000;

  return (
    <CustomCard className="mb-3 pt-1">
      <FieldSet>
        <Row>
          <Col md={12}>
            <Legend>
              <h4 className="mb-0 fw-bold">{t("customerWebsite.footer")}</h4>
            </Legend>
          </Col>
          <Col md={3} className="mt-1">
            <ColorPickerWithTextField
              hideButton
              label={t("customerWebsite.BGColor")}
              fieldName="footer.bgColor"
              fieldError={errors?.footer?.bgColor}
              fieldRef={footerBgColorPickerRef}
              fieldVal={watch("footer.bgColor")}
              displayFieldWatcher={FooterBGColorDisplay}
              fieldWatcherName="footer.bgColorDisplay"
              isDisabled={Boolean(!FooterBGColorDisplay)}
              setDisplay
              isInGroup
              noSpacing
              hideErrMsg
              isFormChanged={isFormChanged}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={12} md={footerLogoType?._id === logoTypes[0]._id ? 4 : 6}>
            <Form.Group className="mb-4" controlId="land">
              <Form.Label>{t("customerWebsite.logo.logoType")}</Form.Label>
              <SelectField
                control={control}
                name="footer.logo.type"
                options={logoTypes}
                getValues={getValues}
                error={errors?.footer?.logo?.type?.name?.message}
              />
            </Form.Group>
          </Col>

          {footerLogoType?._id === logoTypes[0]._id ? (
            <>
              <Col sm={12} md={4}>
                <TextField
                  label={t("customerWebsite.logo.logoText")}
                  placeholder={t("customerWebsite.logo.logoText")}
                  type="text"
                  name="footer.logo.text"
                  errors={errors?.footer?.logo?.text}
                  register={register}
                  multiline
                  noSpacing
                />
              </Col>
              <Col xs={12} md={4}>
                <ColorPickerWithTextField
                  hideButton
                  label={t("customerWebsite.logo.logoColor")}
                  placeholder="#000000"
                  fieldName="footer.logo.color"
                  fieldError={errors?.footer?.logo?.color}
                  fieldRef={footerLogoColorPickerRef}
                  fieldVal={watch("footer.logo.color")}
                  defaultColorValue="#000000"
                  isInGroup
                  noSpacing
                  hideErrMsg
                  isFormChanged={isFormChanged}
                />
              </Col>
            </>
          ) : footerLogoType?._id === logoTypes[1]._id ? (
            <Col xs={12} md={6}>
              <FileInput
                label={t("customerWebsite.logo.logoImage")}
                name="footer.logo.image"
                // resourceId={resourceId}
                parentFolderName={"customer"}
                url={parseResourceSrc(footerLogoImage)}
                urlForPreview={parsePreviewResourceSrc(footerLogoImage)}
                asset={footerLogoImage?.asset}
                isFormChanged={isFormChanged}
                // selectedCategory={getValues("category")}
                // tagName="backgroundimage"
              />
            </Col>
          ) : null}
        </Row>

        <Row>
          <Col sm={12} md={3}>
            <TextField
              label={t("customerWebsite.taglineText")}
              placeholder={t("customerWebsite.taglineText")}
              type="text"
              name="footer.tag_line.text"
              errors={errors?.footer?.tag_line?.text}
              register={register}
              multiline
              noSpacing
            />
          </Col>
          <Col xs={12} md={3}>
            <ColorPickerWithTextField
              hideButton
              label={t("customerWebsite.taglineColor")}
              placeholder="#000000"
              fieldName="footer.tag_line.color"
              fieldError={errors?.footer?.tag_line?.color}
              fieldRef={footerTaglineColorPickerRef}
              fieldVal={watch("footer.tag_line.color")}
              defaultColorValue="#000000"
              isInGroup
              noSpacing
              hideErrMsg
              isFormChanged={isFormChanged}
            />
          </Col>
        </Row>

        <Row className="mt-5">
          <Col md={12} className="mb-3">
            <Legend>
              <h6 className="mb-0 fw-bold mb-1">
                {t("customerWebsite.socialLinks")}
              </h6>
            </Legend>
          </Col>
          <Col sm={12} md={3}>
            <TextField
              label={`${t("customerWebsite.socialLinks")} ${t(
                "customerWebsite.text"
              )}`}
              placeholder={`${t("customerWebsite.socialLinks")} ${t(
                "customerWebsite.text"
              )}`}
              type="text"
              name="footer.footer_links.social_link.text"
              errors={errors?.footer?.footer_links?.social_link?.text}
              register={register}
              multiline
              noSpacing
            />
          </Col>
          <Col md={3}>
            <ColorPickerWithTextField
              hideButton
              label={t("customerWebsite.linksTextColor")}
              placeholder="#000000"
              fieldName="footer.footer_links.social_link.color"
              fieldError={errors?.footer?.footer_links?.social_link?.color}
              fieldRef={footerQuickLinksColorPickerRef}
              fieldVal={watch("footer.footer_links.social_link.color")}
              defaultColorValue="#000000"
              isInGroup
              noSpacing
              hideErrMsg
              isFormChanged={isFormChanged}
            />
          </Col>
          <Col sm={12} md={3}>
            <TextField
              label={t("customerWebsite.fbLink")}
              placeholder={t("customerWebsite.fbLink")}
              type="text"
              errors={errors?.footer?.facebook?.link}
              name="footer.facebook.link"
              register={register}
              noSpacing
            />
          </Col>
          <Col xs={12} md={3}>
            <ColorPickerWithTextField
              hideButton
              label={t("customerWebsite.fbColor")}
              placeholder="#000000"
              fieldName="footer.facebook.color"
              fieldError={errors?.footer?.facebook?.color}
              fieldRef={facebookColorPickerRef}
              fieldVal={watch("footer.facebook.color")}
              defaultColorValue="#000000"
              isInGroup
              noSpacing
              hideErrMsg
              isFormChanged={isFormChanged}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col sm={12} md={3}>
            <TextField
              label={t("customerWebsite.instaLink")}
              placeholder={t("customerWebsite.instaLink")}
              type="text"
              name="footer.instagram.link"
              errors={errors?.footer?.instagram?.link}
              register={register}
              noSpacing
            />
          </Col>
          <Col xs={12} md={3}>
            <ColorPickerWithTextField
              hideButton
              label={t("customerWebsite.instaColor")}
              placeholder="#000000"
              fieldName="footer.instagram.color"
              fieldError={errors?.footer?.instagram?.color}
              fieldRef={instaColorPickerRef}
              fieldVal={watch("footer.instagram.color")}
              defaultColorValue="#000000"
              isInGroup
              noSpacing
              hideErrMsg
              isFormChanged={isFormChanged}
            />
          </Col>
          <Col sm={12} md={3}>
            <TextField
              label={t("customerWebsite.ytLink")}
              placeholder={t("customerWebsite.ytLink")}
              type="text"
              name="footer.youtube.link"
              errors={errors?.footer?.youtube?.link}
              register={register}
              noSpacing
            />
          </Col>
          <Col xs={12} md={3}>
            <ColorPickerWithTextField
              hideButton
              label={t("customerWebsite.ytColor")}
              placeholder="#000000"
              fieldName="footer.youtube.color"
              fieldError={errors?.footer?.youtube?.color}
              fieldRef={youtubeColorPickerRef}
              fieldVal={watch("footer.youtube.color")}
              defaultColorValue="#000000"
              isInGroup
              noSpacing
              hideErrMsg
              isFormChanged={isFormChanged}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col sm={12} md={3}>
            <TextField
              label={t("customerWebsite.x_twitterLink")}
              placeholder={t("customerWebsite.x_twitterLink")}
              type="text"
              name="footer.x_twitter.link"
              errors={errors?.footer?.x_twitter?.link}
              register={register}
              noSpacing
            />
          </Col>
          <Col xs={12} md={3}>
            <ColorPickerWithTextField
              hideButton
              label={t("customerWebsite.x_twitterColor")}
              placeholder="#000000"
              fieldName="footer.x_twitter.color"
              fieldError={errors?.footer?.x_twitter?.color}
              fieldRef={x_twitterColorPickerRef}
              fieldVal={watch("footer.x_twitter.color")}
              defaultColorValue="#000000"
              isInGroup
              noSpacing
              hideErrMsg
              isFormChanged={isFormChanged}
            />
          </Col>
          <Col sm={12} md={3}>
            <TextField
              label={t("customerWebsite.pinterestLink")}
              placeholder={t("customerWebsite.pinterestLink")}
              type="text"
              name="footer.pinterest.link"
              errors={errors?.footer?.pinterest?.link}
              register={register}
              noSpacing
            />
          </Col>
          <Col xs={12} md={3}>
            <ColorPickerWithTextField
              hideButton
              label={t("customerWebsite.pinterestColor")}
              placeholder="#000000"
              fieldName="footer.pinterest.color"
              fieldError={errors?.footer?.pinterest?.color}
              fieldRef={pinterestColorPickerRef}
              fieldVal={watch("footer.pinterest.color")}
              defaultColorValue="#000000"
              isInGroup
              noSpacing
              hideErrMsg
              isFormChanged={isFormChanged}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col sm={12} md={3}>
            <TextField
              label={t("customerWebsite.tiktokLink")}
              placeholder={t("customerWebsite.tiktokLink")}
              type="text"
              name="footer.tiktok.link"
              errors={errors?.footer?.tiktok?.link}
              register={register}
              noSpacing
            />
          </Col>
          <Col xs={12} md={3}>
            <ColorPickerWithTextField
              hideButton
              label={t("customerWebsite.tiktokColor")}
              placeholder="#000000"
              fieldName="footer.tiktok.color"
              fieldError={errors?.footer?.tiktok?.color}
              fieldRef={tiktokColorPickerRef}
              fieldVal={watch("footer.tiktok.color")}
              defaultColorValue="#000000"
              isInGroup
              noSpacing
              hideErrMsg
              isFormChanged={isFormChanged}
            />
          </Col>
          <Col sm={12} md={3}>
            <TextField
              label={t("customerWebsite.linkedinLink")}
              placeholder={t("customerWebsite.linkedinLink")}
              type="text"
              name="footer.linkedin.link"
              errors={errors?.footer?.linkedin?.link}
              register={register}
              noSpacing
            />
          </Col>
          <Col xs={12} md={3}>
            <ColorPickerWithTextField
              hideButton
              label={t("customerWebsite.linkedinColor")}
              placeholder="#000000"
              fieldName="footer.linkedin.color"
              fieldError={errors?.footer?.linkedin?.color}
              fieldRef={linkedinColorPickerRef}
              fieldVal={watch("footer.linkedin.color")}
              defaultColorValue="#000000"
              isInGroup
              noSpacing
              hideErrMsg
              isFormChanged={isFormChanged}
            />
          </Col>
        </Row>
        <Row className="mt-5">
          <Col md={12} className="mb-3">
            <Legend>
              <h6 className="mb-0 fw-bold mb-1">
                {t("customerWebsite.quickLinks")}
              </h6>
            </Legend>
          </Col>
          <Col sm={12} md={3}>
            <TextField
              label={`${t("customerWebsite.quickLinks")} ${t(
                "customerWebsite.text"
              )}`}
              placeholder={`${t("customerWebsite.quickLinks")} ${t(
                "customerWebsite.text"
              )}`}
              type="text"
              name="footer.footer_links.quick_links.text"
              errors={errors?.footer?.footer_links?.quick_links?.text}
              register={register}
              multiline
              noSpacing
            />
          </Col>
          <Col md={3}>
            <div>
              <ColorPickerWithTextField
                hideButton
                label={t("customerWebsite.linksTextColor")}
                placeholder="#000000"
                fieldName="footer.footer_links.quick_links.color"
                fieldError={errors?.footer?.footer_links?.quick_links?.color}
                fieldRef={footerQuickLinksColorPickerRef}
                fieldVal={watch("footer.footer_links.quick_links.color")}
                defaultColorValue="#000000"
                isInGroup
                noSpacing
                hideErrMsg
                isFormChanged={isFormChanged}
              />
            </div>
          </Col>
          <Col md={12}>
            <Row className="mt-3 bg-light py-2 mx-1 border">
              <Col sm={12} md={6}>
                <TextField
                  label={`${t("customerWebsite.link")} #1 ${t(
                    "customerWebsite.text"
                  )}`}
                  placeholder={`${t("customerWebsite.link")} #1 ${t(
                    "customerWebsite.text"
                  )}`}
                  type="text"
                  name="footer.footer_links.quick_links.links[0].text"
                  errors={
                    errors?.footer?.footer_links?.quick_links?.links &&
                    errors?.footer?.footer_links?.quick_links?.links[0]?.text
                  }
                  register={register}
                  multiline
                  noSpacing
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="land">
                  <Form.Label>{`${t("customerWebsite.link")} #1 ${t(
                    "customerWebsite.link"
                  )}`}</Form.Label>
                  <SelectField
                    control={control}
                    name="footer.footer_links.quick_links.links[0].link"
                    options={sections}
                    getValues={getValues}
                    error={
                      errors?.footer?.footer_links?.quick_links?.links[0]?.link
                        ?.name?.message
                    }
                    optionValue="hashId"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3 bg-light py-2 mx-1 border">
              <Col sm={12} md={6}>
                <TextField
                  label={`${t("customerWebsite.link")} #2 ${t(
                    "customerWebsite.text"
                  )}`}
                  placeholder={`${t("customerWebsite.link")} #2 ${t(
                    "customerWebsite.text"
                  )}`}
                  type="text"
                  name="footer.footer_links.quick_links.links[1].text"
                  errors={
                    errors?.footer?.footer_links?.quick_links?.links &&
                    errors?.footer?.footer_links?.quick_links?.links[1]?.text
                  }
                  register={register}
                  multiline
                  noSpacing
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="land">
                  <Form.Label>{`${t("customerWebsite.link")} #2 ${t(
                    "customerWebsite.link"
                  )}`}</Form.Label>
                  <SelectField
                    control={control}
                    name="footer.footer_links.quick_links.links[1].link"
                    options={sections}
                    getValues={getValues}
                    error={
                      errors?.footer?.footer_links?.quick_links?.links[1]?.link
                        ?.name?.message
                    }
                    optionValue="hashId"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3 bg-light py-2 mx-1 border">
              <Col sm={12} md={6}>
                <TextField
                  label={`${t("customerWebsite.link")} #3 ${t(
                    "customerWebsite.text"
                  )}`}
                  placeholder={`${t("customerWebsite.link")} #3 ${t(
                    "customerWebsite.text"
                  )}`}
                  type="text"
                  name="footer.footer_links.quick_links.links[2].text"
                  errors={
                    errors?.footer?.footer_links?.quick_links?.links &&
                    errors?.footer?.footer_links?.quick_links?.links[2]?.text
                  }
                  register={register}
                  multiline
                  noSpacing
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="land">
                  <Form.Label>{`${t("customerWebsite.link")} #3 ${t(
                    "customerWebsite.link"
                  )}`}</Form.Label>
                  <SelectField
                    control={control}
                    name="footer.footer_links.quick_links.links[2].link"
                    options={sections}
                    getValues={getValues}
                    error={
                      errors?.footer?.footer_links?.quick_links?.links[2]?.link
                        ?.name?.message
                    }
                    optionValue="hashId"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3 bg-light py-2 mx-1 border">
              <Col sm={12} md={6}>
                <TextField
                  label={`${t("customerWebsite.link")} #4 ${t(
                    "customerWebsite.text"
                  )}`}
                  placeholder={`${t("customerWebsite.link")} #4 ${t(
                    "customerWebsite.text"
                  )}`}
                  type="text"
                  name="footer.footer_links.quick_links.links[3].text"
                  errors={
                    errors?.footer?.footer_links?.quick_links?.links &&
                    errors?.footer?.footer_links?.quick_links?.links[3]?.text
                  }
                  register={register}
                  multiline
                  noSpacing
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="land">
                  <Form.Label>{`${t("customerWebsite.link")} #4 ${t(
                    "customerWebsite.link"
                  )}`}</Form.Label>
                  <SelectField
                    control={control}
                    name="footer.footer_links.quick_links.links[3].link"
                    options={sections}
                    getValues={getValues}
                    error={
                      errors?.footer?.footer_links?.quick_links?.links[3]?.link
                        ?.name?.message
                    }
                    optionValue="hashId"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="mt-5">
          <Col md={12} className="mb-3">
            <Legend>
              <h6 className="mb-0 fw-bold mb-1">
                {t("customerWebsite.policyLinks")}
              </h6>
            </Legend>
          </Col>
          <Col sm={12} md={3}>
            <TextField
              label={`${t("customerWebsite.policyLinks")} ${t(
                "customerWebsite.text"
              )}`}
              placeholder={`${t("customerWebsite.policyLinks")} ${t(
                "customerWebsite.text"
              )}`}
              type="text"
              name="footer.footer_links.pp_links.text"
              errors={errors?.footer?.footer_links?.pp_links?.text}
              register={register}
              multiline
              noSpacing
            />
          </Col>
          <Col md={3}>
            <div>
              <ColorPickerWithTextField
                hideButton
                label={t("customerWebsite.linksTextColor")}
                placeholder="#000000"
                fieldName="footer.footer_links.pp_links.color"
                fieldError={errors?.footer?.footer_links?.pp_links?.color}
                fieldRef={footerPolicyLinksColorPickerRef}
                fieldVal={watch("footer.footer_links.pp_links.color")}
                defaultColorValue="#000000"
                isInGroup
                noSpacing
                hideErrMsg
                isFormChanged={isFormChanged}
              />
            </div>
          </Col>
          <Col md={12}>
            <Row className="mt-3">
              <Col xs={12}>
                <Form.Check
                  role="switch"
                  type="switch"
                  className="fw-bold"
                  label={t("customerWebsite.legalPagesSwitchLabel")}
                  {...register("footer.footer_links.pp_links.showAsPages")}
                />
                {showLegalPages && (
                  <Form.Text muted>
                    {legalPageUrlList.length
                      ? t("customerWebsite.legalPagesHelp", {
                          urls: legalPageUrlList.join(", "),
                          interpolation: { escapeValue: false }
                        })
                      : t("customerWebsite.legalPagesHelpNone")}
                  </Form.Text>
                )}
              </Col>
            </Row>
            <Row className="mt-3 bg-light py-2 mx-1 border">
              <Col sm={12} md={6}>
                <TextField
                  label={`${t("customerWebsite.privacyPolicy")} ${t(
                    "customerWebsite.text"
                  )}`}
                  placeholder={`${t("customerWebsite.privacyPolicy")} ${t(
                    "customerWebsite.text"
                  )}`}
                  type="text"
                  name="footer.footer_links.pp_links.links[0].text"
                  errors={
                    errors?.footer?.footer_links?.pp_links?.links &&
                    errors?.footer?.footer_links?.pp_links?.links[0]?.text
                  }
                  register={register}
                  multiline
                  noSpacing
                />
              </Col>
              <Col xs={12} md={6}>
                <TextField
                  label={`${t("customerWebsite.privacyPolicy")} ${t(
                    "customerWebsite.link"
                  )}`}
                  placeholder={`${t("customerWebsite.privacyPolicy")} ${t(
                    "customerWebsite.link"
                  )}`}
                  type="text"
                  name="footer.footer_links.pp_links.links[0].link"
                  errors={
                    errors?.footer?.footer_links?.pp_links?.links &&
                    errors?.footer?.footer_links?.pp_links?.links[0]?.link
                  }
                  register={register}
                  noSpacing
                />
              </Col>
              <Col md={12}>
                <EditorModalController
                  title={`${t("customerWebsite.privacyPolicy")}`}
                  context="footer.footer_links.pp_links.links[0].content"
                  maxLength={maxContentLength}
                />
                <LegalPageContentError
                  error={
                    errors?.footer?.footer_links?.pp_links?.links?.[0]?.content
                  }
                  isMissing={legalPageWithoutContent === 0}
                />
              </Col>
            </Row>
            <Row className="mt-3 bg-light py-2 mx-1 border">
              <Col sm={12} md={6}>
                <TextField
                  label={`${t("customerWebsite.legalNotice")} ${t(
                    "customerWebsite.text"
                  )}`}
                  placeholder={`${t("customerWebsite.legalNotice")} ${t(
                    "customerWebsite.text"
                  )}`}
                  type="text"
                  name="footer.footer_links.pp_links.links[1].text"
                  errors={
                    errors?.footer?.footer_links?.pp_links?.links &&
                    errors?.footer?.footer_links?.pp_links?.links[1]?.text
                  }
                  register={register}
                  multiline
                  noSpacing
                />
              </Col>
              <Col xs={12} md={6}>
                <TextField
                  label={`${t("customerWebsite.legalNotice")} ${t(
                    "customerWebsite.link"
                  )}`}
                  placeholder={`${t("customerWebsite.legalNotice")} ${t(
                    "customerWebsite.link"
                  )}`}
                  type="text"
                  name="footer.footer_links.pp_links.links[1].link"
                  errors={
                    errors?.footer?.footer_links?.pp_links?.links &&
                    errors?.footer?.footer_links?.pp_links?.links[1]?.link
                  }
                  register={register}
                  noSpacing
                />
              </Col>
              <Col md={12}>
                <EditorModalController
                  title={`${t("customerWebsite.legalNotice")}`}
                  context="footer.footer_links.pp_links.links[1].content"
                  maxLength={maxContentLength}
                />
                <LegalPageContentError
                  error={
                    errors?.footer?.footer_links?.pp_links?.links?.[1]?.content
                  }
                  isMissing={legalPageWithoutContent === 1}
                />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col xs={12} md={6}>
                <Form.Check
                  role="button"
                  type="switch"
                  className="fw-bold"
                  label={t("customerWebsite.agbSwitchLabel")}
                  {...register("footer.footer_links.pp_links.agbLinkEnabled")}
                />
              </Col>
            </Row>
            <Collapse in={agbLinkEnabled}>
              <div>
                <Row className="mt-3 bg-light py-2 mx-1 border">
                  <Col sm={12} md={6}>
                    <TextField
                      label={`AGB ${t("customerWebsite.text")}`}
                      placeholder={`AGB ${t("customerWebsite.text")}`}
                      type="text"
                      name="footer.footer_links.pp_links.links[2].text"
                      errors={
                        errors?.footer?.footer_links?.pp_links?.links &&
                        errors?.footer?.footer_links?.pp_links?.links[2]?.text
                      }
                      register={register}
                      multiline
                      noSpacing
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <TextField
                      label={`AGB ${t("customerWebsite.link")}`}
                      placeholder={`AGB ${t("customerWebsite.link")}`}
                      type="text"
                      name="footer.footer_links.pp_links.links[2].link"
                      errors={
                        errors?.footer?.footer_links?.pp_links?.links &&
                        errors?.footer?.footer_links?.pp_links?.links[2]?.link
                      }
                      register={register}
                      noSpacing
                    />
                  </Col>
                  <Col md={12}>
                    <EditorModalController
                      title={"AGB"}
                      context="footer.footer_links.pp_links.links[2].content"
                      maxLength={maxContentLength}
                    />
                    <LegalPageContentError
                      error={
                        errors?.footer?.footer_links?.pp_links?.links?.[2]
                          ?.content
                      }
                      isMissing={legalPageWithoutContent === 2}
                    />
                  </Col>
                </Row>
              </div>
            </Collapse>
          </Col>
        </Row>
        <Row className="mt-5">
          <Legend>
            <h6 className="mb-3 fw-bold">{t("customerWebsite.contact")}</h6>
          </Legend>
          <Row>
            <Col sm={12} md={3}>
              <TextField
                label={`${t("customerWebsite.contact")} ${t(
                  "customerWebsite.text"
                )}`}
                placeholder={`${t("customerWebsite.contact")} ${t(
                  "customerWebsite.text"
                )}`}
                type="text"
                name="footer.contact_card.text"
                errors={errors?.footer?.footer_links?.pp_links?.text}
                register={register}
                multiline
                noSpacing
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col sm={12} md={3}>
              <TextField
                label={t("customerWebsite.name")}
                placeholder={t("customerWebsite.name")}
                type="text"
                name="footer.contact_card.name.text"
                errors={errors?.footer?.contact_card?.name?.text}
                register={register}
                multiline
                defaultValue={`${customerData?.firstName || ""} ${
                  customerData?.lastName || ""
                }`.trim()}
                noSpacing
              />
            </Col>
            <Col xs={12} md={3}>
              <ColorPickerWithTextField
                hideButton
                label={t("customerWebsite.nameColor")}
                placeholder="#000000"
                fieldName="footer.contact_card.name.color"
                fieldError={errors?.footer?.contact_card?.name?.color}
                fieldRef={contactCardNameColorPickerRef}
                fieldVal={watch("footer.contact_card.name.color")}
                defaultColorValue="#000000"
                isInGroup
                noSpacing
                hideErrMsg
                isFormChanged={isFormChanged}
              />
            </Col>
            <Col sm={12} md={3}>
              <TextField
                label={t("customerWebsite.title")}
                placeholder={t("customerWebsite.title")}
                type="text"
                name="footer.contact_card.title.text"
                errors={errors?.footer?.contact_card?.title?.text}
                register={register}
                multiline
                noSpacing
              />
            </Col>
            <Col xs={12} md={3}>
              <ColorPickerWithTextField
                hideButton
                label={t("customerWebsite.titleColor")}
                placeholder="#000000"
                fieldName="footer.contact_card.title.color"
                fieldError={errors?.footer?.contact_card?.title?.color}
                fieldRef={contactCardTitleColorPickerRef}
                fieldVal={watch("footer.contact_card.title.color")}
                defaultColorValue="#000000"
                isInGroup
                noSpacing
                hideErrMsg
                isFormChanged={isFormChanged}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col sm={12} md={3}>
              <TextField
                label={t("customerWebsite.ctaBtnText")}
                placeholder={t("customerWebsite.ctaBtnText")}
                type="text"
                name="footer.contact_card.btn.text"
                errors={errors?.footer?.contact_card?.btn?.text}
                register={register}
                multiline
                noSpacing
              />
            </Col>
            <Col sm={12} md={3}>
              <TextField
                label={t("customerWebsite.ctaBtnLink")}
                placeholder={t("customerWebsite.ctaBtnLink")}
                type="text"
                name="footer.contact_card.btn.link"
                errors={errors?.footer?.contact_card?.btn?.link}
                register={register}
                multiline
                noSpacing
              />
            </Col>
            <Col xs={12} md={3}>
              <ColorPickerWithTextField
                hideButton
                label={t("customerWebsite.ctaBtnColor")}
                placeholder="#000000"
                fieldName="footer.contact_card.btn.color"
                fieldError={errors?.footer?.contact_card?.btn?.color}
                fieldRef={contactCardCTAButtonColorPickerRef}
                fieldVal={watch("footer.contact_card.btn.color")}
                defaultColorValue="#ffffff"
                isInGroup
                noSpacing
                hideErrMsg
                isFormChanged={isFormChanged}
              />
            </Col>
            <Col xs={12} md={3}>
              <ColorPickerWithTextField
                hideButton
                label={t("customerWebsite.ctaBtnBGColor")}
                placeholder="#000000"
                fieldName="footer.contact_card.btn.bgColor"
                fieldError={errors?.footer?.contact_card?.btn?.bgColor}
                fieldRef={contactCardCTAButtonBgColorPickerRef}
                fieldVal={watch("footer.contact_card.btn.bgColor")}
                displayFieldWatcher={ContactCardBtnBGColorDisplay}
                fieldWatcherName="footer.contact_card.btn.bgColorDisplay"
                isDisabled={Boolean(!ContactCardBtnBGColorDisplay)}
                setDisplay
                isInGroup
                noSpacing
                hideErrMsg
                isFormChanged={isFormChanged}
              />
            </Col>
          </Row>
          <Row className="ms-1 mt-3">
            <Form.Check
              role="button"
              type="switch"
              className="fw-bold"
              label={`${t("common.enable")} CTA 2 (Optional)`}
              {...register("footer.contact_card.optionalCtaEnabled")}
            />
          </Row>
          <Collapse in={watch("footer.contact_card.optionalCtaEnabled")}>
            <div>
              <Row className="mt-3">
                <Col sm={12} md={3}>
                  <TextField
                    label={t("customerWebsite.ctaBtnTwoText")}
                    placeholder={t("customerWebsite.ctaBtnTwoText")}
                    type="text"
                    name="footer.contact_card.optionalBtn.text"
                    errors={errors?.footer?.contact_card?.optionalBtn?.text}
                    register={register}
                    multiline
                    noSpacing
                  />
                </Col>
                <Col sm={12} md={3}>
                  <TextField
                    label={t("customerWebsite.ctaBtnTwoLink")}
                    placeholder={t("customerWebsite.ctaBtnTwoLink")}
                    type="text"
                    name="footer.contact_card.optionalBtn.link"
                    errors={errors?.footer?.contact_card?.optionalBtn?.link}
                    register={register}
                    multiline
                    noSpacing
                  />
                </Col>
                <Col xs={12} md={3}>
                  <ColorPickerWithTextField
                    hideButton
                    label={t("customerWebsite.ctaBtnTwoColor")}
                    placeholder="#000000"
                    fieldName="footer.contact_card.optionalBtn.color"
                    fieldError={
                      errors?.footer?.contact_card?.optionalBtn?.color
                    }
                    fieldRef={contactCardCTAOptionalButtonColorPickerRef}
                    fieldVal={watch("footer.contact_card.optionalBtn.color")}
                    defaultColorValue="#ffffff"
                    isInGroup
                    noSpacing
                    hideErrMsg
                    isFormChanged={isFormChanged}
                  />
                </Col>
                <Col xs={12} md={3}>
                  <ColorPickerWithTextField
                    hideButton
                    label={t("customerWebsite.ctaBtnTwoBGColor")}
                    placeholder="#000000"
                    fieldName="footer.contact_card.optionalBtn.bgColor"
                    fieldError={
                      errors?.footer?.contact_card?.optionalBtn?.bgColor
                    }
                    fieldRef={contactCardCTAOptionalButtonBgColorPickerRef}
                    fieldVal={watch("footer.contact_card.optionalBtn.bgColor")}
                    displayFieldWatcher={ContactCardOptionalBtnBGColorDisplay}
                    fieldWatcherName="footer.contact_card.optionalBtn.bgColorDisplay"
                    isDisabled={Boolean(!ContactCardOptionalBtnBGColorDisplay)}
                    setDisplay
                    isInGroup
                    noSpacing
                    hideErrMsg
                    isFormChanged={isFormChanged}
                  />
                </Col>
              </Row>
            </div>
          </Collapse>
          <Row className="mt-3">
            <Col sm={12} md={3}>
              <TextField
                label={t("customerWebsite.email")}
                placeholder={t("customerWebsite.email")}
                type="email"
                name="footer.contact_card.email.text"
                errors={errors?.footer?.contact_card?.email?.text}
                register={register}
                defaultValue={customerData?.email}
                noSpacing
              />
            </Col>
            <Col xs={12} md={3}>
              <ColorPickerWithTextField
                hideButton
                label={t("customerWebsite.emailColor")}
                placeholder="#000000"
                fieldName="footer.contact_card.email.color"
                fieldError={errors?.footer?.contact_card?.email?.color}
                fieldRef={contactCardTitleColorPickerRef}
                fieldVal={watch("footer.contact_card.email.color")}
                defaultColorValue="#000000"
                isInGroup
                noSpacing
                hideErrMsg
                isFormChanged={isFormChanged}
              />
            </Col>
            <Col sm={12} md={3}>
              <TextField
                label={`${t("customerWebsite.phone")} (Optional)`}
                placeholder={t("customerWebsite.phone")}
                type="text"
                name="footer.contact_card.phone.text"
                register={register}
                handleKeyDown={(e) => checkPhoneNumFieldFormatOnKeyDown(e)}
                noSpacing
              />
            </Col>
            <Col xs={12} md={3}>
              <ColorPickerWithTextField
                hideButton
                label={t("customerWebsite.phoneColor")}
                placeholder="#000000"
                fieldName="footer.contact_card.phone.color"
                fieldRef={contactCardPhoneColorPickerRef}
                fieldVal={watch("footer.contact_card.phone.color")}
                defaultColorValue="#000000"
                isInGroup
                noSpacing
                isFormChanged={isFormChanged}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col xs={12} md={6}>
              <FileInput
                label={t("customerWebsite.image")}
                name="footer.contact_card.image"
                // resourceId={resourceId}
                parentFolderName={"customer"}
                url={parseResourceSrc(footerContactCardImage)}
                urlForPreview={parsePreviewResourceSrc(footerContactCardImage)}
                error={errors?.footer?.contact_card?.image}
                asset={footerContactCardImage?.asset}
                isFormChanged={isFormChanged}
                // selectedCategory={getValues("category")}
                // tagName="backgroundimage"
              />
            </Col>
          </Row>
        </Row>
        <Row className="mt-5">
          <Legend>
            <h6 className="mb-3 fw-bold">Local Seo Links</h6>
          </Legend>
          <Row>
            <Col xs={12} md={3}>
              <ColorPickerWithTextField
                hideButton
                label={t("customerWebsite.linksTextColor")}
                placeholder="#888888"
                fieldName="footer.local_seo_links.color"
                fieldError={errors?.footer?.local_seo_links?.color}
                fieldRef={localSeoLinksColorPickerRef}
                fieldVal={watch("footer.local_seo_links.color")}
                defaultColorValue="#888888"
                isInGroup
                noSpacing
                hideErrMsg
                isFormChanged={isFormChanged}
              />
            </Col>
          </Row>
          <Row className="mt-4 mb-2">
            <Col sm={12} md={6}>
              <Legend>
                <h6 className="mb-3 fw-bold">
                  A&M {t("customerWebsite.footerBanner")}
                </h6>
              </Legend>
              <Form.Check
                role="button"
                type="switch"
                checked={watch("footer.showAMCreditBanner")}
                label={t("customerWebsite.footerBannerLabel")}
                {...register("footer.showAMCreditBanner")}
              />
            </Col>
          </Row>
        </Row>
      </FieldSet>
    </CustomCard>
  );
};

export default FooterSection;
